import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, DollarSign, Target, Clock, ChevronRight, ArrowUp, ArrowDown, Flame, FileText, Calendar, Users, Award, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';
import { projectsAPI, communityAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';

export default function InvestorDashboard() {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectsAPI.getPortfolio()
      .then(r => setPortfolio(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full" /></div>;

  if (!portfolio) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <Target size={48} className="text-zinc-600 mx-auto mb-4" />
        <h2 className="font-display font-bold text-2xl text-white mb-2">No Investments Yet</h2>
        <p className="text-zinc-500 mb-6">Discover rising talent and back them from just $1.</p>
        <Link to="/projects"><Button className="bg-[#b8ff00] text-black font-bold rounded-full px-8 py-5">Browse Rising Stars</Button></Link>
      </div>
    </div>
  );

  const roiPositive = portfolio.total_roi_pct >= 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display font-black text-3xl text-white mb-1" data-testid="portfolio-title">Your Portfolio</h1>
          <p className="text-zinc-500 text-sm">Track your investments and estimated returns</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Invested', value: formatCurrency(portfolio.total_invested), icon: DollarSign, color: 'text-[#b8ff00]', bg: 'bg-[#b8ff00]/10' },
            { label: 'Estimated Value', value: formatCurrency(portfolio.total_estimated_value), icon: TrendingUp, color: roiPositive ? 'text-emerald-400' : 'text-red-400', bg: roiPositive ? 'bg-emerald-500/10' : 'bg-red-500/10' },
            { label: 'ROI', value: `${roiPositive ? '+' : ''}${portfolio.total_roi_pct}%`, icon: roiPositive ? ArrowUp : ArrowDown, color: roiPositive ? 'text-emerald-400' : 'text-red-400', bg: roiPositive ? 'bg-emerald-500/10' : 'bg-red-500/10' },
            { label: 'Projects Backed', value: portfolio.total_projects, icon: Target, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          ].map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
                className="p-4 rounded-xl bg-zinc-900 border border-zinc-800" data-testid={`portfolio-stat-${idx}`}>
                <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center mb-2`}>
                  <Icon size={16} className={stat.color} />
                </div>
                <p className={`font-display font-bold text-2xl ${stat.color}`}>{stat.value}</p>
                <p className="text-[11px] text-zinc-500">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-[1fr,380px] gap-6">
          {/* Investments list */}
          <div>
            <h2 className="font-bold text-lg text-white mb-4 flex items-center gap-2"><Wallet size={18} className="text-[#b8ff00]" /> Investments</h2>
            <div className="space-y-3">
              {portfolio.investments.map((inv, idx) => {
                const invRoiPositive = inv.roi_pct >= 0;
                return (
                  <motion.div key={inv.id || idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Link to={`/projects/${inv.project_id}`} className="block p-4 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-600 group transition-all" data-testid={`investment-${idx}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm text-white group-hover:text-[#b8ff00] transition-colors truncate">{inv.project_name}</h3>
                          <p className="text-[11px] text-zinc-500">{inv.creator_name} · {inv.project_genre}</p>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${inv.project_status === 'funded' ? 'bg-emerald-500/10 text-emerald-400' : inv.project_status === 'active' ? 'bg-blue-500/10 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>
                          {inv.project_status}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-center">
                        <div>
                          <p className="text-[10px] text-zinc-600">Invested</p>
                          <p className="text-xs font-bold text-white">{formatCurrency(inv.amount)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-zinc-600">Est. Value</p>
                          <p className={`text-xs font-bold ${invRoiPositive ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(inv.estimated_value)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-zinc-600">ROI</p>
                          <p className={`text-xs font-bold flex items-center justify-center gap-0.5 ${invRoiPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                            {invRoiPositive ? <ArrowUp size={10} /> : <ArrowDown size={10} />} {inv.roi_pct}%
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-zinc-600">Funded</p>
                          <p className="text-xs font-bold text-zinc-300">{inv.funded_pct}%</p>
                        </div>
                      </div>
                      {inv.discovery_score > 0 && (
                        <div className="mt-2 flex items-center gap-2 text-[10px] text-zinc-600">
                          <Flame size={10} className="text-orange-400" /> Discovery Score: {inv.discovery_score}
                        </div>
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {portfolio.investments.length === 0 && (
              <div className="text-center py-12">
                <Target size={36} className="text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm mb-4">Back a project to start building your portfolio</p>
                <Link to="/projects"><Button className="bg-[#b8ff00] text-black font-bold rounded-full px-6" data-testid="browse-projects-cta">Browse Rising Stars</Button></Link>
              </div>
            )}
          </div>

          {/* Sidebar: Updates + Genre breakdown */}
          <div className="space-y-4">
            {/* Recent updates from backed projects */}
            {portfolio.recent_updates?.length > 0 && (
              <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4" data-testid="portfolio-updates">
                <h3 className="font-bold text-sm text-white flex items-center gap-2 mb-3"><FileText size={14} className="text-[#b8ff00]" /> Updates from Your Projects</h3>
                <div className="space-y-2">
                  {portfolio.recent_updates.map((upd, idx) => (
                    <Link key={upd.id} to={`/projects/${upd.project_id}`} className="block p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors" data-testid={`portfolio-update-${idx}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${upd.update_type === 'milestone' ? 'bg-amber-500/10 text-amber-400' : upd.update_type === 'media' ? 'bg-violet-500/10 text-violet-400' : 'bg-zinc-700 text-zinc-400'}`}>
                          {upd.update_type}
                        </span>
                        <span className="text-[9px] text-zinc-600">{new Date(upd.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs font-semibold text-white">{upd.title}</p>
                      <p className="text-[10px] text-zinc-500 line-clamp-2">{upd.content}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Genre breakdown */}
            {Object.keys(portfolio.by_genre || {}).length > 0 && (
              <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4" data-testid="portfolio-genres">
                <h3 className="font-bold text-sm text-white flex items-center gap-2 mb-3"><Award size={14} className="text-amber-400" /> By Genre</h3>
                <div className="space-y-2">
                  {Object.entries(portfolio.by_genre).sort((a, b) => b[1] - a[1]).map(([genre, amount]) => {
                    const pct = Math.round((amount / portfolio.total_invested) * 100);
                    return (
                      <div key={genre} className="flex items-center gap-3">
                        <span className="text-xs text-zinc-400 capitalize w-16 truncate">{genre}</span>
                        <div className="flex-1 bg-zinc-800 rounded-full h-2">
                          <div className="h-2 rounded-full bg-gradient-to-r from-[#b8ff00] to-emerald-400" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-zinc-500 w-12 text-right">{formatCurrency(amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-[#b8ff00]/10 to-emerald-500/5 rounded-xl border border-[#b8ff00]/20 p-4 text-center">
              <h3 className="font-bold text-sm text-white mb-2">Discover more talent</h3>
              <p className="text-[11px] text-zinc-400 mb-3">New Rising Stars every day.</p>
              <Link to="/projects"><Button className="bg-[#b8ff00] text-black font-bold rounded-full px-5 text-xs" data-testid="discover-more-cta">Browse Rising Stars <ChevronRight size={12} className="ml-1" /></Button></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
